import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Send, Loader2, MapPin, Calendar, Train, Plane, Bus, Check, ArrowRight, MessageCircle } from 'lucide-react';

import Mascot from './Mascot';
import {
    useLeadBotState,
    recordNudge,
    snoozeLeadBot,
    isLeadBotSnoozed,
    markLeadSubmitted,
} from './leadBotStore';
import {
    BOT_NAME,
    LIMITS,
    SEARCH_TRIGGERS,
    HOME_TRIGGERS,
    PAIN_TRIGGERS,
    NUDGES,
    NUDGE_PRIMARY,
    MOOD_FOR_TRIGGER,
    COPY,
    FLEX_OPTIONS,
    MODE_OPTIONS,
    BOT_TRIGGER_TYPE,
    MASCOT_THEME,
    FAB_COPY,
    shortPlace,
} from './leadBotScript';
import { searchLocations } from './locationSearch';
import { useContactLead } from '../../hooks/useContactLead';
import { FEATURES } from '../../config/features';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../../config/contactConfig';
import { getCallStatus, describeNextOpen } from '../../utils/callHours';
import './LeadBot.css';

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────
const wait = (ms) => new Promise(r => setTimeout(r, ms));
let msgSeq = 0;
const msgId = () => `m${++msgSeq}`;

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => toISO(new Date());
const addDays = (iso, n) => {
    const [y, m, d] = iso.split('-').map(Number);
    return toISO(new Date(y, m - 1, d + n));
};
const prettyShort = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
const prettyLong = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const phoneRule = (code) => COUNTRY_CODES.find(c => c.code === code) || { minLen: 6, maxLen: 14 };
const validatePhone = (code, digits) => {
    if (!digits) return 'Enter your mobile number';
    const { minLen, maxLen } = phoneRule(code);
    if (digits.length < minLen || digits.length > maxLen) {
        return minLen === maxLen ? `Enter a ${minLen}-digit mobile number` : `Enter a ${minLen} to ${maxLen} digit mobile number`;
    }
    if (code === '+91' && !/^[6-9]/.test(digits)) return 'Indian mobile numbers start with 6, 7, 8 or 9';
    return '';
};

const TRAIN_CLASSES = ['1A', '2A', '3A', 'SL', 'CC', '2S'];
const FLIGHT_CLASSES = ['Economy', 'Premium', 'Business', 'First'];

const tripClassLabel = (s) => {
    const classes = s?.seat_classes || [];
    const modes = s?.modes || [];
    if (modes.length === 1 && modes[0] === 'Train') return classes.find(c => TRAIN_CLASSES.includes(c)) || '';
    if (modes.length === 1 && modes[0] === 'Flight') return classes.find(c => FLIGHT_CLASSES.includes(c)) || '';
    return '';
};

const normalizeModes = (modes) => {
    const list = Array.isArray(modes) ? modes : [];
    if (list.includes('All')) return [...MODE_OPTIONS];
    return MODE_OPTIONS.filter(m => list.includes(m));
};

const MODE_ICONS = { Train, Flight: Plane, Bus };

const tripSummary = (s) => {
    if (!s) return 'this trip';
    const cls = tripClassLabel(s);
    return `${shortPlace(s.src_name)} → ${shortPlace(s.dest_name)}, ${prettyShort(s.travel_date_from)}${cls ? `, ${cls}` : ''}`;
};

const joinList = (arr) => (arr.length <= 1 ? arr.join('') : `${arr.slice(0, -1).join(', ')} or ${arr[arr.length - 1]}`);

const FLEX_TEXT = {
    exact: 'Fixed date.',
    pm1: 'Dates are flexible by 1 day either side.',
    pm3: 'Dates are flexible by 2 to 3 days either side.',
};

// Lead message written from what the user picked in the chat
const buildMessage = ({ intent, src, dest, from, to, baseDate, modes, classes, flex }) => {
    if (!src || !dest) return 'Please call me back. I need help planning a trip. (via RouteChef assistant)';
    const route = `from ${shortPlace(src)} to ${shortPlace(dest)}`;
    const when = from !== to
        ? `around ${prettyLong(baseDate)} (any day between ${prettyLong(from)} and ${prettyLong(to)})`
        : `on ${prettyLong(from)}`;
    const how = modes.length ? ` by ${joinList(modes.map(m => m.toLowerCase()))}` : '';
    const cls = classes.length ? `, preferred class ${classes.join(' / ')}` : '';
    const flexLine = FLEX_TEXT[flex] || '';
    const ask = intent === 'seat_alert'
        ? `Seat alert: please WhatsApp me when a confirmed seat opens ${route} ${when}${how}${cls}.`
        : `Please help me get confirmed tickets ${route} ${when}${how}${cls}.`;
    return `${ask} ${flexLine} (via RouteChef assistant)`.replace(/\s+/g, ' ').trim();
};

// Keeps the sheet above the mobile keyboard
const useKeyboardInset = (active) => {
    const [inset, setInset] = useState(0);
    useEffect(() => {
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        if (!active || !vv) { setInset(0); return undefined; }
        const update = () => setInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
        };
    }, [active]);
    return inset;
};

// ───────────────────────────────────────────────────────────────────────────
// Small UI pieces
// ───────────────────────────────────────────────────────────────────────────
const Chips = ({ options, onPick }) => (
    <div className="flex flex-wrap gap-2">
        {options.map(o => (
            <button
                key={o.value}
                type="button"
                onClick={() => onPick(o)}
                className={`min-h-[40px] px-3.5 rounded-full border text-sm font-medium transition-colors
                    ${o.primary
                        ? 'bg-orange-500 border-orange-500 text-white hover:bg-orange-600 active:bg-orange-700'
                        : 'bg-white border-orange-200 text-orange-700 hover:bg-orange-50 active:bg-orange-100'}
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const TypingDots = () => (
    <div className="flex items-end gap-2">
        <div className="rounded-2xl rounded-bl-md bg-slate-100 px-3.5 py-3 flex gap-1" aria-label={`${BOT_NAME} is typing`}>
            <span className="lb-typing-dot" /><span className="lb-typing-dot lb-d2" /><span className="lb-typing-dot lb-d3" />
        </div>
    </div>
);

const LocationInput = ({ label, value, onChange, autoFocus }) => {
    const [text, setText] = useState(value?.label || '');
    const [focused, setFocused] = useState(false);
    const results = useMemo(() => (focused && !value ? searchLocations(text) : []), [focused, text, value]);

    return (
        <div>
            <div className="relative">
                <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                <input
                    type="text"
                    autoComplete="off"
                    autoFocus={autoFocus}
                    aria-label={label}
                    placeholder={label}
                    value={text}
                    onFocus={(e) => { setFocused(true); if (value) e.target.select(); }}
                    onBlur={() => setTimeout(() => setFocused(false), 150)}
                    onChange={(e) => { setText(e.target.value); if (value) onChange(null); }}
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-base sm:text-sm text-slate-900
                               placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
            </div>
            {results.length > 0 && (
                <ul role="listbox" className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-sm">
                    {results.map(item => (
                        <li
                            key={`${item.type}-${item.code}`}
                            role="option"
                            aria-selected="false"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setText(item.label); onChange(item); setFocused(false); }}
                            className="flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer hover:bg-orange-50"
                        >
                            {item.type === 'airport'
                                ? <Plane size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
                                : <Train size={14} className="shrink-0 text-slate-400" aria-hidden="true" />}
                            <span className="min-w-0 flex-1 truncate text-slate-800">{item.name}</span>
                            <span className="shrink-0 text-xs font-semibold text-slate-500">{item.code}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const RouteStep = ({ onSubmit }) => {
    const [src, setSrc] = useState(null);
    const [dest, setDest] = useState(null);
    const [date, setDate] = useState(addDays(todayISO(), 1));
    const ready = src && dest && date && src.code !== dest.code;

    return (
        <div className="space-y-2">
            <LocationInput label="From: station, airport or city" value={src} onChange={setSrc} autoFocus />
            <LocationInput label="To: station, airport or city" value={dest} onChange={setDest} />
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                    <input
                        type="date"
                        aria-label="Travel date"
                        value={date}
                        min={todayISO()}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-2 text-base sm:text-sm text-slate-900
                                   focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                </div>
                <button
                    type="button"
                    disabled={!ready}
                    onClick={() => onSubmit({ src, dest, date })}
                    className="shrink-0 inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold
                               disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next <ArrowRight size={15} aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

const ModePicker = ({ initial, onSubmit }) => {
    const [picked, setPicked] = useState(initial.length ? initial : ['Train']);
    const toggle = (m) => setPicked(prev => (prev.includes(m)
        ? prev.filter(x => x !== m)
        : MODE_OPTIONS.filter(x => x === m || prev.includes(x))));

    return (
        <div className="space-y-2.5">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Modes of travel">
                {MODE_OPTIONS.map(m => {
                    const Icon = MODE_ICONS[m];
                    const on = picked.includes(m);
                    return (
                        <button
                            key={m}
                            type="button"
                            aria-pressed={on}
                            onClick={() => toggle(m)}
                            className={`inline-flex items-center gap-1.5 min-h-[40px] px-3.5 rounded-full border text-sm font-medium transition-colors
                                ${on
                                    ? 'bg-orange-500 border-orange-500 text-white'
                                    : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300'}`}
                        >
                            {on ? <Check size={14} aria-hidden="true" /> : <Icon size={14} aria-hidden="true" />}
                            {m}
                        </button>
                    );
                })}
            </div>
            <button
                type="button"
                disabled={!picked.length}
                onClick={() => onSubmit(picked)}
                className="w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold
                           disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Continue <ArrowRight size={15} aria-hidden="true" />
            </button>
        </div>
    );
};

const PhoneStep = ({
    intent, countryCode, setCountryCode, phone, setPhone, error, busy, onSubmit,
}) => (
    <form
        className="space-y-2"
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        noValidate
    >
        <div className="flex gap-2">
            <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                aria-label="Country code"
                className="h-11 w-[92px] shrink-0 rounded-xl border border-slate-200 bg-white pl-2 pr-1 text-base sm:text-sm text-slate-900
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
                {COUNTRY_CODES.map(c => <option key={c.iso} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
            <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                autoFocus
                aria-label="Mobile number"
                aria-invalid={!!error}
                placeholder={countryCode === '+91' ? '98765 43210' : 'Mobile number'}
                value={phone}
                maxLength={18}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                className={`h-11 min-w-0 flex-1 rounded-xl border bg-white px-3 text-base sm:text-sm text-slate-900 placeholder:text-slate-400
                            focus:outline-none focus:ring-2 focus:border-transparent
                            ${error ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:ring-orange-400'}`}
            />
            <button
                type="submit"
                disabled={busy}
                aria-label="Send"
                className="shrink-0 w-11 h-11 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white flex items-center justify-center
                           disabled:opacity-60"
            >
                {busy ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Send size={17} aria-hidden="true" />}
            </button>
        </div>
        {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
        <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <MessageCircle size={13} className="text-green-600" aria-hidden="true" />
            {intent === 'seat_alert' ? 'Alerts come on WhatsApp. No spam, promise.' : COPY.phoneNote}
        </p>
    </form>
);

// ───────────────────────────────────────────────────────────────────────────
// LeadBot
// ───────────────────────────────────────────────────────────────────────────
/**
 * Mount ONCE inside the Router (e.g. in App.jsx, outside <Routes>).
 * Shows on the home page ("/") and on the route search page (which publishes
 * signals via useLeadBotSignals). Hidden everywhere else, including /chat.
 * Leads go through the same submitContactAction as the ContactModal.
 */
const LeadBot = () => {
    const enabled = !!FEATURES.ENABLE_LEAD_BOT;
    const bot = useLeadBotState();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    // Same hook + backend contract as the ContactModal: open (creates the CLICKED row), then submit
    const { loadConfig, callUs, openContactModal, submitContactAction } = useContactLead();

    const page = bot.page === 'search' ? 'search' : (pathname === '/' ? 'home' : null);
    const suppressed = Object.values(bot.suppressed || {}).some(Boolean);

    const [open, setOpen] = useState(false);
    const [bubble, setBubble] = useState(null);       // { trigger, text }
    const [mood, setMood] = useState('idle');
    const [messages, setMessages] = useState([]);
    const [step, setStep] = useState(null);
    const [typing, setTyping] = useState(false);
    const [unread, setUnread] = useState(false);
    const [ready, setReady] = useState(false);         // dock enters after the page has painted
    const [extended, setExtended] = useState(true);    // extended FAB (avatar + label) vs avatar only
    const [greeted, setGreeted] = useState(false);     // first-appearance wave + pulse done
    const [local, setLocal] = useState({ exitIntent: false, homeIdle: false, homeScroll: false });

    const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [busy, setBusy] = useState(false);

    const convRef = useRef(0);
    const leadRef = useRef({});
    const listRef = useRef(null);
    const kbInset = useKeyboardInset(open);

    // Contact config (call hours) for this bot's own hook instance
    useEffect(() => { if (enabled && loadConfig) loadConfig(); }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Leaving an allowed page closes everything
    useEffect(() => {
        setOpen(false);
        setBubble(null);
        setLocal({ exitIntent: false, homeIdle: false, homeScroll: false });
        convRef.current += 1;
    }, [page]);

    // ── Local triggers: exit intent (desktop) + home idle/scroll ──────────
    useEffect(() => {
        if (!enabled || !page) return undefined;
        const start = Date.now();
        const fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
        const onOut = (e) => {
            if (fine && !e.relatedTarget && e.clientY <= 0 && Date.now() - start > 10000) {
                setLocal(l => (l.exitIntent ? l : { ...l, exitIntent: true }));
            }
        };
        document.addEventListener('mouseout', onOut);

        let idleTimer = null;
        let onScroll = null;
        if (page === 'home') {
            idleTimer = setTimeout(() => setLocal(l => ({ ...l, homeIdle: true })), LIMITS.homeIdleMs);
            onScroll = () => {
                if (window.scrollY > LIMITS.homeScrollPx && Date.now() - start > 8000) {
                    setLocal(l => (l.homeScroll ? l : { ...l, homeScroll: true }));
                }
            };
            window.addEventListener('scroll', onScroll, { passive: true });
        }
        return () => {
            document.removeEventListener('mouseout', onOut);
            if (idleTimer) clearTimeout(idleTimer);
            if (onScroll) window.removeEventListener('scroll', onScroll);
        };
    }, [enabled, page]);

    // ── Entrance: let the page paint first, then pop in and wave once ─────
    useEffect(() => {
        if (!enabled || !page) { setReady(false); return undefined; }
        const t1 = setTimeout(() => { setReady(true); setMood('wave'); }, 1200);
        const t2 = setTimeout(() => { setGreeted(true); setMood(m => (m === 'wave' ? 'idle' : m)); }, 4600);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [enabled, page]);

    // ── Extended FAB behaviour (Material style) ───────────────────────────
    // Shrinks to the avatar while scrolling down, grows back on scroll up or near the top.
    // On the search page it starts extended for a few seconds, then stays compact
    // unless the user scrolls up, so it never sits on top of a Book button for long.
    useEffect(() => {
        if (!enabled || !page || open) return undefined;
        setExtended(true);
        let lastY = window.scrollY;
        let idle = null;
        const settle = page === 'search'
            ? setTimeout(() => { if (window.scrollY > 80) setExtended(false); }, 6000)
            : null;

        const onScroll = () => {
            const y = window.scrollY;
            const dy = y - lastY;
            lastY = y;
            if (y < 80) { setExtended(true); return; }
            if (dy > 6) setExtended(false);
            else if (dy < -6) setExtended(true);
            if (page === 'home') {
                clearTimeout(idle);
                idle = setTimeout(() => setExtended(true), 1500);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            clearTimeout(idle);
            if (settle) clearTimeout(settle);
        };
    }, [enabled, page, open]);

    // Lets page CSS move other floating buttons (scroll-to-top) above the bot
    useEffect(() => {
        const on = enabled && !!page && ready;
        document.body.classList.toggle('has-leadbot', on);
        return () => document.body.classList.remove('has-leadbot');
    }, [enabled, page, ready]);

    // ── Pick the strongest active trigger ─────────────────────────────────
    const candidate = useMemo(() => {
        if (!page) return null;
        const active = {
            noRoutes: bot.noRoutes,
            allWaitlisted: bot.allWaitlisted,
            tatkalWindow: bot.tatkalWindow && bot.resultsCount > 0,
            returnedAfterAway: bot.returnedAfterAway,
            dateFlips: bot.dateFlips >= 3,
            deepScrollNoBook: bot.deepScrollNoBook,
            exitIntent: local.exitIntent,
            homeScroll: local.homeScroll,
            homeIdle: local.homeIdle,
        };
        const list = page === 'search' ? SEARCH_TRIGGERS : HOME_TRIGGERS;
        return list.find(t => active[t]) || null;
    }, [page, bot, local]);

    const nudgeKey = page === 'search' ? (bot.searchKey || 'search') : 'home';

    useEffect(() => {
        if (!enabled || !page || !candidate || open || bubble) return undefined;
        if (suppressed || bot.leadSubmitted || bot.resultsLoading) return undefined;
        if (bot.nudgesShown >= LIMITS.maxNudgesPerSession) return undefined;
        if (bot.nudgedKeys.includes(nudgeKey) || isLeadBotSnoozed()) return undefined;

        const t = setTimeout(() => {
            setBubble({ trigger: candidate, text: NUDGES[candidate](bot.search) });
            setMood(MOOD_FOR_TRIGGER(candidate));
            recordNudge(nudgeKey);
        }, LIMITS.nudgeDelayMs);
        return () => clearTimeout(t);
    }, [enabled, page, candidate, open, bubble, suppressed, bot.leadSubmitted, bot.resultsLoading, bot.nudgesShown, nudgeKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Bubble auto-hides into an unread dot; hides instantly if something else takes over the screen
    useEffect(() => {
        if (!bubble) return undefined;
        if (suppressed) { setBubble(null); setUnread(true); return undefined; }
        const t = setTimeout(() => { setBubble(null); setUnread(true); setMood('idle'); }, LIMITS.bubbleAutoHideMs);
        return () => clearTimeout(t);
    }, [bubble, suppressed]);

    // Keep the chat scrolled to the latest line
    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, typing, step]);

    // Esc closes
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Conversation engine ───────────────────────────────────────────────
    const pushUser = (text) => setMessages(m => [...m, { id: msgId(), from: 'user', text }]);

    const say = useCallback(async (lines, nextStep, moodAfter = 'idle') => {
        const conv = convRef.current;
        setStep(null);
        for (const line of lines) {
            setTyping(true);
            setMood('think');
            await wait(450 + Math.min(line.length * 9, 750));
            if (convRef.current !== conv) return;
            setMessages(m => [...m, { id: msgId(), from: 'bot', text: line }]);
        }
        setTyping(false);
        setStep(nextStep);
        setMood(moodAfter);
    }, []);

    const callLine = () => {
        const status = getCallStatus(callUs);
        const next = describeNextOpen(status.nextOpen, callUs);
        return status.open || !next
            ? 'Our travel team will call you back shortly.'
            : `Our lines open ${next}, we'll call you then.`;
    };

    const closePanel = () => {
        convRef.current += 1;
        setOpen(false);
        setTyping(false);
        setMood('idle');
    };

    const goConfirmTrip = () => {
        const s = leadRef.current.search;
        if (!s) { say([COPY.askRoute], 'route'); return; }
        say([COPY.confirmTrip(tripSummary(s)), COPY.askFlex], 'flex');
    };

    const goAlert = () => {
        leadRef.current.intent = 'seat_alert';
        say([COPY.askPhoneAlert], 'phone');
    };

    const goHandoff = (label) => {
        pushUser(label);
        say([COPY.handoffChat], 'end', 'wave').then(() => {
            setTimeout(() => { closePanel(); navigate('/chat'); }, 700);
        });
    };

    const startConversation = ({ fromNudge = false, accept = false } = {}) => {
        convRef.current += 1;
        const trigger = fromNudge ? bubble?.trigger : null;
        const nudgeText = fromNudge ? bubble?.text : null;

        leadRef.current = {
            trigger: trigger || 'manual',
            search: page === 'search' ? bot.search : null,
            intent: 'callback',          // callback | seat_alert
            flex: 'exact',
            modes: null,
            src: null,
            dest: null,
            date: '',
        };
        setBubble(null);
        setUnread(false);
        setOpen(true);
        setPhone('');
        setPhoneError('');
        setBusy(false);
        setStep(null);

        if (bot.leadSubmitted) {
            setMessages([]);
            say([COPY.alreadyHave], 'already', 'idle');
            return;
        }

        // Log the open as a CLICKED lead row (trigger_type = bot_*), exactly like a banner click
        try {
            openContactModal?.(BOT_TRIGGER_TYPE[trigger || 'manual'] || BOT_TRIGGER_TYPE.manual, bot.search || {});
        } catch { /* lead logging must never break the chat */ }

        if (fromNudge) {
            setMessages([{ id: msgId(), from: 'bot', text: nudgeText }]);
            if (accept) {
                pushUser(NUDGE_PRIMARY(trigger));
                if (page === 'home') say(['Pick one and I will take it from there.'], 'homeMenu', 'wave');
                else goConfirmTrip();
            } else {
                pushUser('No thanks');
                say([COPY.alertOffer], 'alertOffer');
            }
            return;
        }

        setMessages([]);
        if (page === 'home') say([COPY.homeGreeting], 'homeMenu', 'wave');
        else say([COPY.searchGreeting(bot.search)], 'searchMenu', 'wave');
    };

    // Bubble actions
    const onBubbleYes = () => startConversation({ fromNudge: true, accept: true });
    const onBubbleNo = () => {
        if (bubble && PAIN_TRIGGERS.includes(bubble.trigger)) {
            startConversation({ fromNudge: true, accept: false });
        } else {
            setBubble(null);
            setMood('idle');
        }
    };
    const onBubbleClose = () => {
        snoozeLeadBot(LIMITS.snoozeHours);
        setBubble(null);
        setMood('idle');
    };

    // Step handlers
    const onHomeMenu = (o) => {
        if (o.value === 'plan') return goHandoff(o.label);
        pushUser(o.label);
        if (o.value === 'seats') return say([COPY.askRoute], 'route');
        leadRef.current.intent = 'callback';
        return say([COPY.askPhoneCallback(callLine())], 'phone');
    };

    const onSearchMenu = (o) => {
        if (o.value === 'plan') return goHandoff(o.label);
        pushUser(o.label);
        if (o.value === 'alert') return goAlert();
        return goConfirmTrip();
    };

    const onRoute = ({ src, dest, date }) => {
        Object.assign(leadRef.current, { src, dest, date });
        pushUser(`${shortPlace(src.label)} → ${shortPlace(dest.label)}, ${prettyShort(date)}`);
        say([COPY.askFlex], 'flex');
    };

    const onFlex = (o) => {
        leadRef.current.flex = o.value;
        pushUser(o.label);
        say([COPY.askModes], 'modes');
    };

    const onModes = (modes) => {
        leadRef.current.modes = modes;
        leadRef.current.intent = 'callback';
        pushUser(modes.join(' + '));
        say([COPY.askPhoneCallback(callLine())], 'phone');
    };

    const onAlertOffer = (o) => {
        pushUser(o.label);
        if (o.value === 'yes') return goAlert();
        return say([COPY.bye], 'end', 'wave');
    };

    const buildFormData = (digits) => {
        const l = leadRef.current;
        const s = l.search || {};
        const srcName = l.src ? l.src.label : (s.src_name || '');
        const srcCode = l.src ? l.src.code : (s.src_code || '');
        const destName = l.dest ? l.dest.label : (s.dest_name || '');
        const destCode = l.dest ? l.dest.code : (s.dest_code || '');
        const hasRoute = !!(srcName && destName);

        const today = todayISO();
        const base = l.date || s.travel_date_from || today;
        const days = (FLEX_OPTIONS.find(o => o.value === l.flex) || {}).days || 0;
        const fromRaw = addDays(base, -days);
        const from = fromRaw < today ? today : fromRaw;
        const to = addDays(base, days);

        const searchModes = normalizeModes(s.modes);
        const modes = l.modes || (hasRoute ? (searchModes.length ? searchModes : [...MODE_OPTIONS]) : []);

        // Keep only the classes that match the modes the user picked
        const classes = (s.seat_classes || []).filter(c =>
            (modes.includes('Train') && TRAIN_CLASSES.includes(c)) ||
            (modes.includes('Flight') && FLIGHT_CLASSES.includes(c)));

        return {
            src_name: srcName,
            src_code: srcCode,
            dest_name: destName,
            dest_code: destCode,
            travel_date_from: hasRoute ? from : '',
            travel_date_to: hasRoute ? to : '',
            modes,
            seat_classes: classes,
            phone: digits,
            country_code: countryCode,
            message: buildMessage({
                intent: l.intent, src: srcName, dest: destName, from, to, baseDate: base, modes, classes, flex: l.flex,
            }),
            // Extra bot fields. contact_lead_handler stores them when the hook passes them through;
            // lead_source is also inferred server side from trigger_type = bot_*.
            lead_source: 'lead_bot',
            trigger_type: BOT_TRIGGER_TYPE[l.trigger] || BOT_TRIGGER_TYPE.manual,
            bot_intent: l.intent,
            bot_page: page,
            date_flex: l.flex,
        };
    };

    const submitPhone = async () => {
        const digits = phone.replace(/\D/g, '');
        const err = validatePhone(countryCode, digits);
        if (err) { setPhoneError(err); return; }
        setPhoneError('');
        setBusy(true);

        const intent = leadRef.current.intent;
        let ok = false;
        // Backend statuses stay the same two: the bot always submits as a callback request
        try { ok = await submitContactAction('callback_request', buildFormData(digits)); } catch { ok = false; }
        setBusy(false);
        if (!ok) { setPhoneError(COPY.submitError); return; }

        markLeadSubmitted();
        const phoneText = `${countryCode} ${digits}`;
        pushUser(phoneText);
        const status = getCallStatus(callUs);
        const next = describeNextOpen(status.nextOpen, callUs);
        const when = status.open || !next ? 'shortly' : `when our lines open ${next}`;
        say([intent === 'seat_alert' ? COPY.doneAlert(phoneText) : COPY.doneCallback(phoneText, when)], 'done', 'celebrate');
    };

    // ── Render ────────────────────────────────────────────────────────────
    if (!enabled || !page || typeof document === 'undefined') return null;

    const dockHidden = suppressed && !open;

    const renderControls = () => {
        if (typing || !step) return null;
        switch (step) {
            case 'homeMenu':
                return <Chips onPick={onHomeMenu} options={[
                    { value: 'seats', label: 'Need confirmed tickets', primary: true },
                    { value: 'plan', label: 'Plan a trip' },
                    { value: 'talk', label: 'Talk to a person' },
                ]} />;
            case 'searchMenu':
                return <Chips onPick={onSearchMenu} options={[
                    { value: 'seats', label: 'Get confirmed seats', primary: true },
                    { value: 'alert', label: 'Alert me if seats open' },
                    { value: 'plan', label: 'Plan another trip' },
                ]} />;
            case 'route':
                return <RouteStep onSubmit={onRoute} />;
            case 'flex':
                return <Chips onPick={onFlex} options={FLEX_OPTIONS} />;
            case 'modes':
                return <ModePicker initial={normalizeModes(leadRef.current.search?.modes)} onSubmit={onModes} />;
            case 'alertOffer':
                return <Chips onPick={onAlertOffer} options={[
                    { value: 'yes', label: 'Yes, alert me', primary: true },
                    { value: 'no', label: 'Not now' },
                ]} />;
            case 'phone':
                return (
                    <PhoneStep
                        intent={leadRef.current.intent}
                        countryCode={countryCode}
                        setCountryCode={(v) => { setCountryCode(v); setPhoneError(''); }}
                        phone={phone}
                        setPhone={(v) => { setPhone(v); setPhoneError(''); }}
                        error={phoneError}
                        busy={busy}
                        onSubmit={submitPhone}
                    />
                );
            case 'done':
            case 'already':
                return <Chips onPick={(o) => (o.value === 'plan' ? goHandoff(o.label) : closePanel())} options={[
                    { value: 'close', label: 'Thanks!', primary: true },
                    ...(page === 'home' ? [{ value: 'plan', label: 'Plan a trip meanwhile' }] : []),
                ]} />;
            case 'end':
            default:
                return <Chips onPick={closePanel} options={[{ value: 'close', label: 'Close' }]} />;
        }
    };

    return createPortal(
        <>
            {/* Dock: extended FAB + nudge bubble, bottom-right (where users expect chat) */}
            {!open && ready && (
                <div
                    className={`lb-dock fixed z-[900] right-4 sm:right-6 bottom-[calc(16px+env(safe-area-inset-bottom))] sm:bottom-6
                                flex flex-col items-end ${dockHidden ? 'lb-hidden' : ''}`}
                    aria-hidden={dockHidden}
                >
                    {bubble && (
                        <div
                            role="status"
                            className="lb-bubble mb-3 w-[min(290px,calc(100vw-32px))] rounded-2xl rounded-br-md
                                       bg-white border border-slate-100 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.35)] p-4"
                        >
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[13px] font-semibold text-slate-900">{BOT_NAME}</span>
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> Online
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={onBubbleClose}
                                aria-label="Dismiss for today"
                                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                            >
                                <X size={14} />
                            </button>
                            <p className="pr-4 text-sm leading-snug text-slate-700">{bubble.text}</p>
                            <div className="mt-3 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={onBubbleYes}
                                    className="min-h-[40px] px-4 rounded-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white text-sm font-semibold shadow-sm shadow-orange-500/30"
                                >
                                    {NUDGE_PRIMARY(bubble.trigger)}
                                </button>
                                <button
                                    type="button"
                                    onClick={onBubbleNo}
                                    className="min-h-[40px] px-3 rounded-full text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                >
                                    No thanks
                                </button>
                            </div>
                        </div>
                    )}

                    {(() => {
                        // While a bubble is talking, the FAB stays compact so there's one message at a time
                        const showLabel = extended && !bubble;
                        const label = FAB_COPY({ page, leadSubmitted: bot.leadSubmitted, pain: bot.noRoutes || bot.allWaitlisted });
                        return (
                            <button
                                type="button"
                                onClick={() => startConversation()}
                                tabIndex={dockHidden ? -1 : 0}
                                aria-label={`${label.title} ${label.sub}`}
                                className={`lb-fab group relative flex items-center rounded-full bg-white border border-slate-200/80
                                            shadow-[0_10px_30px_-8px_rgba(15,23,42,0.35)] hover:shadow-[0_14px_36px_-8px_rgba(15,23,42,0.4)]
                                            focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2
                                            ${showLabel ? 'pl-4 pr-1.5' : 'p-1.5'}`}
                            >
                                <span className={`lb-fab-label overflow-hidden text-left ${showLabel ? 'lb-fab-label--on' : ''}`}>
                                    <span className="block whitespace-nowrap text-[13px] sm:text-sm font-semibold text-slate-900 leading-tight">{label.title}</span>
                                    <span className="block whitespace-nowrap text-[12px] sm:text-[13px] text-slate-500 leading-tight mt-0.5">{label.sub}</span>
                                </span>

                                <span className="relative shrink-0 w-[60px] h-[60px] sm:w-[68px] sm:h-[68px] rounded-full
                                                 bg-gradient-to-br from-sky-50 to-teal-100 flex items-center justify-center">
                                    {!greeted && <span className="lb-ring" aria-hidden="true" />}
                                    <Mascot mood={bubble ? mood : (greeted ? 'idle' : mood)} size={58} theme={MASCOT_THEME} className="sm:hidden" />
                                    <Mascot mood={bubble ? mood : (greeted ? 'idle' : mood)} size={66} theme={MASCOT_THEME} className="hidden sm:block" />
                                    {unread && (
                                        <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-red-500 border-2 border-white
                                                         text-[11px] font-bold text-white flex items-center justify-center" aria-hidden="true">1</span>
                                    )}
                                </span>
                            </button>
                        );
                    })()}
                </div>
            )}

            {open && (
                <>
                    {/* Mobile backdrop: tap to close */}
                    <div className="sm:hidden fixed inset-0 z-[940] bg-slate-900/25 lb-fade" onClick={closePanel} aria-hidden="true" />

                    <div
                        role="dialog"
                        aria-label={`${BOT_NAME}, RouteChef travel help`}
                        style={kbInset ? { bottom: kbInset } : undefined}
                        className="lb-sheet fixed z-[950] inset-x-0 bottom-0 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px]
                                   max-h-[75dvh] sm:max-h-[min(580px,80vh)] flex flex-col bg-white
                                   rounded-t-3xl sm:rounded-2xl shadow-2xl border border-slate-100"
                    >
                        {/* Header */}
                        <div className="shrink-0 flex items-center gap-3 px-4 pt-3 pb-3 border-b border-slate-100">
                            <div className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-sky-50 to-teal-100 flex items-center justify-center">
                                <Mascot mood={mood} size={46} theme={MASCOT_THEME} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900 leading-tight">{BOT_NAME}</p>
                                <p className="flex items-center gap-1 text-xs text-slate-500 leading-tight mt-0.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> RouteChef travel help · Online
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closePanel}
                                aria-label="Close chat"
                                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Messages */}
                        <div ref={listRef} className="flex-1 min-h-[140px] overflow-y-auto overscroll-contain px-4 py-4 space-y-2.5" aria-live="polite">
                            {messages.map(m => (
                                <div key={m.id} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'} lb-msg-in`}>
                                    <div
                                        className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-snug rounded-2xl
                                            ${m.from === 'user'
                                                ? 'bg-orange-500 text-white rounded-br-md'
                                                : 'bg-slate-100 text-slate-800 rounded-bl-md'}`}
                                    >
                                        {m.text}
                                    </div>
                                </div>
                            ))}
                            {typing && <TypingDots />}
                        </div>

                        {/* Controls for the current step */}
                        <div className="shrink-0 border-t border-slate-100 px-4 pt-3 pb-[max(0.875rem,env(safe-area-inset-bottom))] min-h-[64px]">
                            {renderControls()}
                        </div>
                    </div>
                </>
            )}
        </>,
        document.body
    );
};

export default LeadBot;
